import React, { useState, useEffect } from 'react';
import ProjectModal from './ProjectModal';
import { projectService } from '../services/api';

const ProjectManager = () => {
  const [projects, setProjects] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  useEffect(() => {
    loadProjects();
  }, []);
  
  const loadProjects = async () => {
    setLoading(true);
    try {
      const response = await projectService.getProjects();
      console.log('Projects response:', response);
      if (response.data && response.data.data) {
        setProjects(response.data.data);
      } else if (response.data) {
        setProjects(Array.isArray(response.data) ? response.data : []);
      }
    } catch (err) {
      console.error('Failed to load projects:', err);
      setError('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreateProject = async (projectData) => {
    setError('');
    setSuccess('');
    
    try {
      const response = await projectService.createProject(projectData);
      console.log('Create project response:', response);
      
      setSuccess('Project created successfully!');
      setIsModalOpen(false);
      setEditingProject(null);
      
      await loadProjects();
    } catch (err) {
      console.error('Failed to create project:', err);
      
      if (err.response) {
        const { status, data } = err.response;
        if (status === 400) {
          setError(data.message || 'Please check all fields are filled correctly');
        } else if (status === 409) {
          setError('Project name already exists. Please choose a different name.');
        } else if (status === 403) {
          setError('You do not have permission to create projects.');
        } else {
          setError(`Server error (${status}): ${data.message || 'Please try again later'}`);
        }
      } else {
        setError('Network error: Please check your internet connection and try again.');
      }
    }
  };
  
  const handleEditProject = async (projectData) => {
    setError('');
    setSuccess('');
    
    try {
      await projectService.updateProject(editingProject.id, projectData);
      setSuccess('Project updated successfully!');
      setIsModalOpen(false);
      setEditingProject(null);
      
      await loadProjects();
    } catch (err) {
      console.error('Failed to update project:', err);
      setError(err.response?.data?.message || 'Failed to update project');
    }
  };
  
  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project? This action cannot be undone and will affect all associated tasks.')) {
      return;
    }

    try {
      await projectService.deleteProject(projectId);
      setProjects(prev => prev.filter(project => project.id !== projectId));
      console.log('Project deleted successfully');
    } catch (error) {
      console.error('Failed to delete project:', error);
      setError('Failed to delete project. Please try again.');
    }
  };
  
  const openCreateModal = () => {
    setEditingProject(null);
    setIsModalOpen(true);
    setError('');
    setSuccess('');
  };
  
  const openEditModal = (project) => {
    setEditingProject(project);
    setIsModalOpen(true);
    setError('');
    setSuccess('');
  };
  
  const closeModal = () => {
    setIsModalOpen(false);
    setEditingProject(null);
    setError('');
    setSuccess('');
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 1: return 'Planning';
      case 2: return 'In Progress';
      case 3: return 'Completed';
      case 4: return 'On Hold';
      default: return 'Unknown';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 1: return '#6c757d';
      case 2: return '#007bff';
      case 3: return '#28a745';
      case 4: return '#ffc107';
      default: return '#6c757d';
    }
  };
  
  return (
    <div className="project-manager">
      <div className="project-header">
        <h2>Project Management</h2>
        <button className="create-project-btn" onClick={openCreateModal}>
          <i className="fas fa-plus"></i> Create Project
        </button>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      
      {loading ? (
        <div className="loading-message">
          <i className="fas fa-spinner fa-spin"></i> Loading projects...
        </div>
      ) : (
        <div className="projects-grid">
          {projects.length === 0 ? (
            <div className="empty-state">
              <p>No projects found. Create your first project to get started!</p>
            </div>
          ) : (
            projects.map((project) => (
              <div key={project.id} className="project-card">
                <div className="project-card-header">
                  <h3>{project.name}</h3>
                  <div className="project-actions">
                    <button 
                      className="edit-btn"
                      onClick={() => openEditModal(project)}
                      title="Edit Project"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      className="delete-btn"
                      onClick={() => handleDeleteProject(project.id)}
                      title="Delete Project"
                    >
                      <i className="fas fa-trash-alt"></i>
                    </button>
                  </div>
                </div>
                
                <div className="project-description">
                  {project.description || 'No description provided'}
                </div>

                {project.vision && (
                  <div className="project-vision">
                    <strong>Vision:</strong> {project.vision}
                  </div>
                )}

                {project.mission && (
                  <div className="project-mission">
                    <strong>Mission:</strong> {project.mission}
                  </div>
                )}
                
                <div className="project-meta">
                  <span>Created: {new Date(project.created_at).toLocaleDateString()}</span>
                  <span 
                    className="project-status"
                    style={{ backgroundColor: getStatusColor(project.status) }}
                  >
                    {getStatusLabel(project.status)}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      )}
      
      <ProjectModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSubmit={editingProject ? handleEditProject : handleCreateProject}
        initialData={editingProject}
      />
    </div>
  );
};

export default ProjectManager; 